import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';
import posthog from 'posthog-js';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useWhatsNew } from '../hooks/useWhatsNew';
import type { AnnouncementConfig, AnnouncementVariant } from '../types';

const variantLabels: Record<AnnouncementVariant, string> = {
  feature: 'New Feature',
  update: 'Update',
  info: 'Info',
};

const variantColors: Record<AnnouncementVariant, string> = {
  feature: 'bg-lime/20 text-lime border-lime/30',
  update: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  info: 'bg-white/10 text-white/70 border-white/20',
};

interface AnnouncementCardProps {
  announcement: AnnouncementConfig;
  isDismissed: boolean;
}

function AnnouncementCard({ announcement, isDismissed }: AnnouncementCardProps) {
  const navigate = useNavigate();
  const { content } = announcement;
  const variant = content.variant ?? 'info';

  const handleCtaClick = () => {
    if (content.cta?.to) {
      navigate(content.cta.to);
    } else if (content.cta?.href) {
      window.open(content.cta.href, '_blank', 'noopener,noreferrer');
    } else if (content.cta?.onClick) {
      content.cta.onClick();
    }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-card2 p-6 transition-colors hover:border-white/20">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {/* Header with variant badge and status */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={variantColors[variant]}>
              {variantLabels[variant]}
            </Badge>
            {isDismissed ? (
              <Badge variant="outline" className="border-white/20 bg-white/5 text-white/50">
                <CheckCircle2 className="mr-1 h-3 w-3" />
                Dismissed
              </Badge>
            ) : (
              <Badge className="border-lime/50 bg-lime/20 text-lime">New</Badge>
            )}
          </div>

          {/* Title and description */}
          <h3 className="mb-2 text-xl font-semibold text-white">{content.title}</h3>
          <p className="mb-4 text-white/70">{content.description}</p>

          {/* CTA */}
          {content.cta && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCtaClick}
              className="group -ml-2 text-lime hover:bg-lime/10 hover:text-lime"
            >
              {content.cta.label}
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          )}
        </div>

        {/* Optional image thumbnail */}
        {content.image && (
          <div className="hidden shrink-0 sm:block">
            <img
              src={content.image}
              alt={content.title}
              className="h-20 w-32 rounded-md object-cover opacity-80"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * What's New page showing all announcements (including dismissed).
 * Provides a persistent view of all feature announcements.
 */
export function WhatsNewPage() {
  const { allAnnouncements, isLoading } = useWhatsNew();

  // Track page view
  useEffect(() => {
    try {
      posthog.capture('whats_new_page_viewed', {
        total_announcements: allAnnouncements.length,
      });
    } catch (e) {
      console.error('Error tracking page view:', e);
    }
  }, [allAnnouncements.length]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="mb-2 flex items-center gap-3">
          <Sparkles className="h-8 w-8 text-lime" />
          <h1 className="text-3xl font-bold text-white">What's New</h1>
        </div>
        <p className="text-white/60">
          Stay up to date with the latest features and updates to the Watchful platform.
        </p>
      </div>

      {/* Announcements list */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-lime border-t-transparent" />
        </div>
      ) : allAnnouncements.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-card2 p-8 text-center">
          <Sparkles className="mx-auto mb-4 h-12 w-12 text-white/30" />
          <h3 className="mb-2 text-lg font-medium text-white/70">No announcements yet</h3>
          <p className="text-white/50">
            Check back later for updates on new features and improvements.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {allAnnouncements.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              isDismissed={announcement.isDismissed}
            />
          ))}
        </div>
      )}

      {/* Back link */}
      <div className="mt-8">
        <Link
          to="/"
          className="text-sm text-white/50 transition-colors hover:text-white/70"
        >
          &larr; Back to Home
        </Link>
      </div>
    </div>
  );
}
